// src/pages/TemplateBuilder.js
import React, { useEffect, useState } from "react";
import Toolbar from "../components/Toolbar";
import BuilderHeader from "../components/BuilderHeader";
import PageCard from "../components/PageCard";
import LivePreview from "../components/LivePreview";
import { getItemAtPath, removeItemAtPath, insertItemAtPath } from "../utils/dndPathHelpers";
import SectionRow from "../components/SectionRow";
import QuestionRow from "../components/QuestionRow";
import { useBuilderStore } from "../store/builderStore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import "../styles/TemplateBuilder.css";
import AddFloatingMenu from "../components/AddFloatingMenu";

export default function TemplateBuilder() {
  // Top-level cards in the builder are conceptually "pages",
  // even though the store key is still `sections`.
  const pages = useBuilderStore((s) => s.sections);
  const meta = useBuilderStore((s) => s.meta);
  const setSections = useBuilderStore((s) => s.setSections);
  const setMeta = useBuilderStore((s) => s.setMeta);
  const undo = useBuilderStore((s) => s.undo);
  const redo = useBuilderStore((s) => s.redo);
  const history = useBuilderStore((s) => s.history);
  const future = useBuilderStore((s) => s.future);
  const resetStore = useBuilderStore((s) => s.resetStore);
  const templateSets = useBuilderStore((s) => s.templateSets);
  const globalSets = useBuilderStore((s) => s.globalSets);
  const setTemplateSets = useBuilderStore((s) => s.setTemplateSets);
  const setGlobalSets = useBuilderStore((s) => s.setGlobalSets);
  const activeSectionId = useBuilderStore((s) => s.activeSectionId);

  const access = meta.access || "all";
  const setAccess = (newAccess) => setMeta({ access: newAccess });

  const { templateId } = useParams();
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(true);
  const [showBottomAddMenu, setShowBottomAddMenu] = useState(false);
  const isMobile = window.innerWidth < 768;

  // Disable scrolling while mobile sheet is open + allow ESC close
  useEffect(() => {
    if (showBottomAddMenu && isMobile) {
      document.body.style.overflow = "hidden";

      const escHandler = (e) => {
        if (e.key === "Escape") setShowBottomAddMenu(false);
      };
      document.addEventListener("keydown", escHandler);

      return () => {
        document.body.style.overflow = "";
        document.removeEventListener("keydown", escHandler);
      };
    }
  }, [showBottomAddMenu, isMobile]);

  // ============================================================
  // HELPERS — FIND CURRENT PAGE BASED ON ACTIVE ROW / SELECTION
  // ============================================================

  const getParentPageIdForRow = (rowId) => {
    if (!rowId) return null;

    const pagesArray = useBuilderStore.getState().sections;

    const walk = (items, parentPageId) => {
      for (const item of items) {
        if (item.id === rowId) return parentPageId;
        if (item.type === "section" && Array.isArray(item.items)) {
          const found = walk(item.items, parentPageId);
          if (found) return found;
        }
      }
      return null;
    };

    for (const page of pagesArray) {
      if (page.id === rowId) return page.id;
      const found = walk(page.items || [], page.id);
      if (found) return found;
    }

    return null;
  };

  const getCurrentPageId = () => {
    const state = useBuilderStore.getState();
    const pagesArray = state.sections;
    const activeRowId = state.activeRowId;
    const activeSectionId = state.activeSectionId;

    if (activeRowId) {
      return getParentPageIdForRow(String(activeRowId));
    }

    if (activeSectionId && pagesArray.some((p) => p.id === activeSectionId)) {
      return activeSectionId;
    }

    if (activeSectionId) {
      const parentPage = getParentPageIdForRow(String(activeSectionId));
      if (parentPage) return parentPage;
    }

    if (pagesArray.length > 0) {
      return pagesArray[pagesArray.length - 1].id;
    }

    const newId = state.addPageAtEnd();
    return newId;
  };

  // ============================================================
  // UPDATED ADD NEW HANDLERS (SAFETYCULTURE ACCURATE)
  // ============================================================

  const handleAddNewQuestion = () => {
    const state = useBuilderStore.getState();
    const activeRowId = state.activeRowId;

    if (activeRowId) {
      state.insertQuestionBelow(activeRowId, "short_text");
      setShowBottomAddMenu(false);
      return;
    }

    const pageId = getCurrentPageId();
    state.addQuestion(pageId, "short_text");
    setShowBottomAddMenu(false);
  };

  const handleAddNewSection = () => {
    const state = useBuilderStore.getState();
    const activeRowId = state.activeRowId;

    if (activeRowId) {
      state.insertSectionBelow(activeRowId);
      setShowBottomAddMenu(false);
      return;
    }

    const pageId = getCurrentPageId();
    state.addSectionRow(pageId);
    setShowBottomAddMenu(false);
  };

  const handleAddNewPage = () => {
    const state = useBuilderStore.getState();
    state.addPageAtEnd();
    setShowBottomAddMenu(false);
  };

  // Floating Add Menu
  const activeRowId = useBuilderStore((s) => s.activeRowId);
  const insertQuestionBelow = useBuilderStore((s) => s.insertQuestionBelow);
  const insertSectionBelow = useBuilderStore((s) => s.insertSectionBelow);

  const rowRefs = React.useRef(new Map());
  const [floatingPos, setFloatingPos] = useState(null);
  const [floatingVisible, setFloatingVisible] = useState(false);

  useEffect(() => {
    // Expose resolver so LivePreview and FillInspection can resolve sets
    window.__builderSetsResolver = (q) =>
      useBuilderStore.getState().resolveOptionsForQuestion(q);
  }, []);

  // If no templateId, start a brand-new template
  useEffect(() => {
    if (!templateId || templateId === "undefined" || templateId === "null") {
      resetStore();
    }
  }, [templateId, resetStore]);

  // Load existing template for editing
  // ============================================================
  // MIGRATION: Convert old sections[].questions[] → new sections[].items[]
  // ============================================================
  const migrateQuestionsToItems = (questions = []) => {
    return questions.map((q) => ({
      id: String(q.id),
      type: "question",
      question: {
        ...q,
        id: String(q.id),
      },
    }));
  };

  const migrateOldSection = (section) => {
    // Already new format → return as-is
    if (Array.isArray(section.items)) return section;

    // Old format → convert questions[] to items[]
    const items = migrateQuestionsToItems(section.questions || []);

    return {
      ...section,
      type: "section",
      items,
    };
  };

  const migrateOldSections = (sections = []) => {
    return sections.map((sec) => migrateOldSection(sec));
  };
  
  useEffect(() => {
    (async () => {
      try {
        if (!templateId || templateId === "undefined" || templateId === "null") return;
        const data = await api.get(`/templates/${templateId}`);
        if (!data) throw new Error("Template not found");

        const firstPage =
          Array.isArray(data.pages) && data.pages.length > 0 ? data.pages[0] : {};

        const normalizedMeta = {
          title: data.title || firstPage.title || "Untitled Template",
          description: data.description || firstPage.description || "",
          logo: data.logo || firstPage.logo || null,
          access: data.access || "all",
        };
        setMeta(normalizedMeta);

        // old normalizeIds removed (handled by migration)

        // MIGRATE OLD formats into new nested items[]
        const migrated = migrateOldSections(firstPage.sections || []);

        // Normalize IDs
        const normalized = migrated.map((s) => ({
          ...s,
          id: String(s.id),
        }));

        // Attach the migrated structure as the page's items[]
        const pageWithItems = {
          id: String(firstPage.id || "page-1"),
          type: "section",
          title: firstPage.title || "Untitled Page",
          description: firstPage.description || "",
          items: normalized,
        };

        // Save into builder store
        setSections([pageWithItems]);

        // NEW: hydrate response sets if present
        if (data.responseSets) {
          if (Array.isArray(data.responseSets.templateSets)) {
            setTemplateSets(data.responseSets.templateSets);
          }
          if (Array.isArray(data.responseSets.globalSets)) {
            setGlobalSets(data.responseSets.globalSets);
          }
        }
      } catch (err) {
        alert("Failed to load template for editing: " + err.message);
        navigate("/templates");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Update floating Add Menu position whenever active row changes
  useEffect(() => {
    if (!activeRowId) {
      setFloatingVisible(false);
      return;
    }

    const el = rowRefs.current.get(String(activeRowId));
    if (!el) {
      setFloatingVisible(false);
      return;
    }

    const rect = el.getBoundingClientRect();

    // SC-style offset: menu left side, vertically centered
    const left = isMobile ? rect.right + 8 : rect.left - 170;
    const top = rect.top + rect.height / 2 - 40;

    setFloatingPos({
      top: top + window.scrollY,
      left: left + window.scrollX,
    });

    setFloatingVisible(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRowId, pages, pages.length]);

  useEffect(() => {
    // After pages change, force-refetch the rowRef for the active row
    setTimeout(() => {
      if (activeRowId) {
        const el = rowRefs.current.get(String(activeRowId));
        if (el) {
          const rect = el.getBoundingClientRect();
          const left = isMobile ? rect.right + 8 : rect.left - 170;
          const top = rect.top + rect.height / 2 - 40;

          setFloatingPos({
            top: top + window.scrollY,
            left: left + window.scrollX,
          });
          setFloatingVisible(true);
        }
      }
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [pages, activeRowId]);

  // ============================================================
  // NEW SAVE FORMAT — PRESERVE REAL NESTED items[] TREE
  // ============================================================
  const serializeItems = (items = []) => {
    return items.map((item) => {
      if (item.type === "question") {
        return {
          id: item.id,
          type: "question",
          question: item.question,
        };
      }

      if (item.type === "section") {
        return {
          id: item.id,
          type: "section",
          title: item.title || "",
          description: item.description || "",
          repeatable: item.repeatable || false,
          items: serializeItems(item.items || []),
        };
      }

      return item;
    });
  };

  const serializeSections = (pages = []) => {
    return pages.map((page) => ({
      id: page.id,
      type: "section",
      title: page.title || page.label || "Untitled Page",
      description: page.description || "",
      repeatable: page.repeatable || false,
      items: serializeItems(page.items || []),
    }));
  };

  const handlePublish = async () => {
    try {
      if (!meta?.title || pages.length === 0) {
        alert("Template must have a title and at least one page.");
        return;
      }

      const payload = {
        title: meta.title,
        description: meta.description,
        logo: meta.logo,
        access,
        responseSets: {
          templateSets,
          globalSets,
        },
        pages: [
          {
            title: meta.title,
            description: meta.description,
            logo: meta.logo,
            // NEW: save true nested items[] structure
            sections: serializeSections(pages),
          },
        ],
      };

      if (templateId && !isNaN(Number(templateId))) {
        await api.put(`/templates/${templateId}`, payload);
        navigate("/templates", { state: { toast: "Template updated" } });
      } else {
        await api.post("/templates", payload);
        navigate("/templates", { state: { toast: "Template saved" } });
      }
    } catch (err) {
      alert("Publish failed: " + err.message);
    }
  };

  // ============================================================
  // SAFETYCULTURE-STYLE RECURSIVE RENDERER WITH COLLAPSE + INDENT LINES
  // ============================================================
  const renderItems = (items, parentPath, depth = 0, parentSectionId = null) => {
    if (!Array.isArray(items)) return null;

    const collapsed = useBuilderStore.getState().collapsedSections;

    return items.map((item, index) => {
      const currentPath = `${parentPath}.${index}`;
      const draggableId = `item-${item.id}`;
      const droppableId = `${currentPath}-children`;

      const isPage = depth === 0 && item.type === "section";
      const isCollapsed = collapsed[item.id] === true;
      const isSectionActive =
        item.type === "section" && activeSectionId === String(item.id);

      const showChildren = !isCollapsed;

      const indentOffset = depth * 16;

      return (
        <Draggable key={draggableId} draggableId={draggableId} index={index}>
          {(dragProvided, dragSnapshot) => (
            <div
              ref={dragProvided.innerRef}
              {...dragProvided.draggableProps}
              style={{
                position: "relative",
                ...(dragProvided.draggableProps.style || {}),
                zIndex: dragSnapshot.isDragging ? 50 : "auto",
              }}
            >
              <div style={{ marginLeft: item.type === "question" ? indentOffset + 8 : indentOffset }}>
                {/* PAGE */}
                {isPage && (
                  <div style={{ marginTop: index === 0 ? 0 : 8 }}>
                    <PageCard
                      section={item}
                      dragHandleProps={dragProvided.dragHandleProps}
                      isCollapsed={isCollapsed}
                      isActive={isSectionActive}
                      onToggleCollapse={() =>
                        useBuilderStore.getState().toggleSectionCollapse(item.id)
                      }
                      onTitleChange={(val) =>
                        useBuilderStore.getState().renameSection(item.id, val)
                      }
                      onDescriptionChange={(val) =>
                        useBuilderStore.getState().setSectionDescription(item.id, val)
                      }
                      onToggleRepeat={() =>
                        useBuilderStore.getState().toggleSectionRepeat(item.id)
                      }
                      onDuplicate={() =>
                        useBuilderStore.getState().duplicateSection(item.id)
                      }
                      onDelete={() =>
                        useBuilderStore.getState().deleteSection(item.id)
                      }
                      onClick={() =>
                        useBuilderStore.getState().setActiveSection(item.id)
                      }
                    />
                  </div>
                )}

                {/* NESTED SECTION */}
                {!isPage && item.type === "section" && (
                  <SectionRow
                    section={item}
                    indentLevel={depth}
                    isCollapsed={isCollapsed}
                    isActive={isSectionActive}
                      rowRef={(el) => {
                        if (el) rowRefs.current.set(String(item.id), el);
                      }}
                    dragHandleProps={dragProvided.dragHandleProps}
                    onToggleCollapse={() =>
                      useBuilderStore.getState().toggleSectionCollapse(item.id)
                    }
                    onTitleChange={(val) =>
                      useBuilderStore.getState().renameSection(item.id, val)
                    }
                    onDescriptionChange={(val) =>
                      useBuilderStore.getState().setSectionDescription(item.id, val)
                    }
                    onToggleRepeat={() =>
                      useBuilderStore.getState().toggleSectionRepeat(item.id)
                    }
                    onDuplicate={() =>
                      useBuilderStore.getState().duplicateSection(item.id)
                    }
                    onDelete={() =>
                      useBuilderStore.getState().deleteSection(item.id)
                    }
                    onClick={() =>
                      useBuilderStore.getState().selectSectionAndChildren(item.id)
                    }
                  />
                )}

                {/* QUESTION */}
                {item.type === "question" && (
                  <QuestionRow
                    sectionId={parentSectionId}
                    question={item.question}
                    rowRef={(el) => {
                      if (el) rowRefs.current.set(String(item.id), el);
                    }}
                    dragHandleProps={dragProvided.dragHandleProps}
                    indentLevel={depth}
                    isSectionCollapsed={false}
                  />
                )}
              </div>

              {/* CHILDREN */}
              {item.items && (
                <Droppable droppableId={droppableId} type="ITEM">
                  {(dropProvided) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      style={{}}
                    >
                      {showChildren &&
                        renderItems(item.items, `${currentPath}.items`, depth + 1, item.id)}
                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
            </div>
          )}
        </Draggable>
      );
    });
  };

  const onDragEnd = (result) => {
    if (!result || !result.destination) return;

    const { source, destination, type } = result;
    if (type !== "ITEM") return;

    const state = useBuilderStore.getState();
    const pages = state.sections;

    const cleanId = (id) => id.replace("-children", "");

    // Convert droppableId path back to store path
    const sourcePath = cleanId(source.droppableId).replace("sections", "sections.items");
    const destPath = cleanId(destination.droppableId).replace("sections", "sections.items");

    const item = getItemAtPath(pages, sourcePath, source.index);
    if (!item) return;

    const removed = removeItemAtPath(pages, sourcePath, source.index);
    const inserted = insertItemAtPath(removed, destPath, destination.index, item);

    useBuilderStore.setState({ sections: inserted });
  };

  return (
    <div className="template-builder-outer">
      <Toolbar
        undo={undo}
        redo={redo}
        canUndo={history.length > 0}
        canRedo={future.length > 0}
        onPublish={handlePublish}
        access={access}
        setAccess={setAccess}
        previewVisible={!isMobile && showPreview}
        onTogglePreview={() => !isMobile && setShowPreview((v) => !v)}
      />

      <div className="template-builder-main">
        <div className="builder-canvas px-3 md:px-0">
          <div className="builder-canvas-inner">
            <BuilderHeader />

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable-root" type="ITEM">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ minHeight: 50 }}
                  >
                    {renderItems(pages, "sections", 0, null)}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {!isMobile && (
            <div style={{ marginTop: 16 }}>
              <div style={{ position: "relative", display: "inline-block" }}>
                <button
                  className="add-section-btn"
                  onClick={() => setShowBottomAddMenu((v) => !v)}
                >
                  + Add New
                </button>
              </div>
            </div>
            )}

            {showBottomAddMenu && (
              <>
                {/* Overlay behind sheet */}
                {isMobile && (
                  <div
                    onTouchStart={(e) => (window.__touchStartY = e.touches[0].clientY)}
                    onTouchMove={(e) => {
                      const diff = e.touches[0].clientY - window.__touchStartY;
                      if (diff > 60) setShowBottomAddMenu(false);
                    }}
                    onClick={() => setShowBottomAddMenu(false)}
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "rgba(0,0,0,0.45)",
                      backdropFilter: "blur(1px)",
                      zIndex: 2500,
                    }}
                  />
                )}
                {/* Desktop dropdown */}
                {!isMobile && (
                  <div
                    className="bg-white border border-gray-200 shadow-md rounded-md mt-2 text-sm"
                    style={{
                      position: "absolute",
                      left: 0,
                      zIndex: 50,
                      minWidth: 180,
                    }}
                  >
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      onClick={handleAddNewQuestion}
                    >
                      + Add question
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      onClick={handleAddNewSection}
                    >
                      + Add section
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      onClick={handleAddNewPage}
                    >
                      + Add page
                    </button>
                  </div>
                )}

                {/* Mobile bottom sheet */}
                {isMobile && (
                  <div
                    style={{
                      position: "fixed",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: "#fff",
                      borderTopLeftRadius: "16px",
                      borderTopRightRadius: "16px",
                      padding: "16px",
                      boxShadow: "0 -4px 16px rgba(0,0,0,0.15)",
                      zIndex: 3000,
                      transform: "translateY(0%)",
                      animation: "slideUp 0.25s ease-out",
                    }}
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "4px",
                        background: "#ccc",
                        borderRadius: "2px",
                        margin: "0 auto 12px auto",
                      }}
                    />

                    <button
                      className="w-full text-left px-3 py-3 text-lg hover:bg-gray-100"
                      onClick={handleAddNewQuestion}
                    >
                      + Add question
                    </button>

                    <button
                      className="w-full text-left px-3 py-3 text-lg hover:bg-gray-100"
                      onClick={handleAddNewSection}
                    >
                      + Add section
                    </button>

                    <button
                      className="w-full text-left px-3 py-3 text-lg hover:bg-gray-100"
                      onClick={handleAddNewPage}
                    >
                      + Add page
                    </button>

                    <button
                      onClick={() => setShowBottomAddMenu(false)}
                      className="w-full text-center mt-3 py-2 text-gray-500 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {showPreview && <LivePreview sections={pages} />}

        {floatingVisible && floatingPos && (
          <AddFloatingMenu
            visible={floatingVisible}
            position={floatingPos}
            onAddQuestion={() => activeRowId && insertQuestionBelow(activeRowId)}
            onAddSection={() => activeRowId && insertSectionBelow(activeRowId)}
          />
        )}
        {isMobile && (
          <button
            onClick={() => setShowBottomAddMenu(true)}
            style={{
              position: "fixed",
              bottom: "24px",
              right: "24px",
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: "#2563eb",
              color: "#fff",
              fontSize: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000
            }}
          >
            +
          </button>
        )}
      </div>
    </div>
  );
}
